import { useState } from 'react'
import { X, CreditCard, Clock, AlertTriangle, Check, Gift } from 'lucide-react'
import { PLANS } from '../../hooks/useBilling'

const TRIAL_PRESETS = [7, 14, 30]

const STATUS_LABELS = {
  trialing: 'Trialing',
  active: 'Active',
  past_due: 'Past due',
  cancelled: 'Cancelled',
  comped: 'Comped',
}
const STATUS_COLORS = {
  trialing: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  past_due: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
  comped: 'bg-purple-100 text-purple-700',
}

function fmtDate(d) {
  return d.toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function ManagePlanModal({ org, branchCount, onSave, onClose }) {
  const [plan, setPlan] = useState(org.plan || 'starter')
  const [trialDays, setTrialDays] = useState(0)
  const [customDays, setCustomDays] = useState('')
  const [useCustom, setUseCustom] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const wasComped = org.subscriptionStatus === 'comped'
  const [comped, setComped] = useState(wasComped)
  const compChanged = comped !== wasComped

  const effectiveDays = useCustom ? parseInt(customDays) || 0 : trialDays
  const planDef = PLANS.find((p) => p.tier === plan)
  const planChanged = plan !== (org.plan || 'starter')
  const hasChanges = planChanged || effectiveDays > 0 || compChanged

  // Downgrade guardrail: the DB trigger would reject this anyway — fail early
  // with a readable reason instead of a raw 23514.
  const overLimit = planDef?.maxBranches != null && branchCount > planDef.maxBranches

  const trialEnd = effectiveDays > 0
    ? new Date(Date.now() + effectiveDays * 24 * 60 * 60 * 1000)
    : null

  const trialExpired = !!org.trialEndsAt && Date.now() > org.trialEndsAt.getTime()

  async function handleConfirm() {
    setSaving(true)
    setError('')
    const result = await onSave(org.id, {
      plan: planChanged ? plan : null,
      trialDays: effectiveDays,
      comped: compChanged ? comped : null,
    })
    setSaving(false)
    if (result?.success === false) {
      setError(result.message || 'Failed to save changes.')
      setConfirming(false)
      return
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-lg max-h-[95vh] sm:max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-800">Manage Plan &amp; Trial</h2>
            <p className="text-xs text-gray-500 mt-0.5">{org.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-6">

          {/* Current state */}
          <div className="flex flex-wrap items-center gap-2 text-xs bg-gray-50 rounded-xl px-3 py-2.5">
            <span className={`px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[org.subscriptionStatus] || 'bg-gray-100 text-gray-600'}`}>
              {STATUS_LABELS[org.subscriptionStatus] || org.subscriptionStatus}
            </span>
            <span className="text-gray-500">
              Current plan: <span className="font-semibold text-gray-700 capitalize">{org.plan || 'starter'}</span>
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500">{branchCount} branch{branchCount !== 1 ? 'es' : ''}</span>
            {org.trialEndsAt && (
              <>
                <span className="text-gray-400">·</span>
                <span className={trialExpired ? 'text-red-600 font-medium' : 'text-gray-500'}>
                  Trial {trialExpired ? 'expired' : 'ends'} {fmtDate(org.trialEndsAt)}
                </span>
              </>
            )}
          </div>

          {/* Plan picker */}
          <div className="space-y-2">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 pb-1 border-b border-gray-100">
              <CreditCard className="w-4 h-4 text-blue-500" /> Plan
            </h3>
            {PLANS.map((p) => (
              <button
                key={p.tier}
                onClick={() => setPlan(p.tier)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-left transition ${
                  plan === p.tier ? 'border-blue-600 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div>
                  <p className="text-sm font-semibold text-gray-800">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.description}</p>
                </div>
                {plan === p.tier && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
              </button>
            ))}
            {overLimit && (
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  This organisation has {branchCount} branches, but {planDef.name} allows max {planDef.maxBranches}.
                  Remove branches first, or pick a bigger plan.
                </span>
              </div>
            )}
          </div>

          {/* Comped access */}
          <div className="space-y-2">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 pb-1 border-b border-gray-100">
              <Gift className="w-4 h-4 text-purple-500" /> Comped access
            </h3>
            <label className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer transition ${
              comped ? 'border-purple-400 bg-purple-50/60' : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input type="checkbox" checked={comped} onChange={(e) => setComped(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-purple-600" />
              <span className="text-xs text-gray-600">
                <span className="font-semibold text-gray-800 block text-sm">Free access, no billing</span>
                Never locked by trial expiry or payment state, and counts ₦0 toward MRR.
                Unticking drops the organisation back to trial rules.
              </span>
            </label>
          </div>

          {/* Trial extension */}
          <div className={`space-y-2 ${comped ? 'opacity-50' : ''}`}>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 pb-1 border-b border-gray-100">
              <Clock className="w-4 h-4 text-blue-500" /> Grant trial days
              {comped && <span className="text-[10px] font-normal text-gray-400">(irrelevant while comped)</span>}
            </h3>
            <p className="text-xs text-gray-500">
              Sets the trial to end <span className="font-medium">N days from today</span> for everyone in
              this organisation, and unlocks it if the trial had expired or the subscription lapsed.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setTrialDays(0); setUseCustom(false); setCustomDays('') }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  effectiveDays === 0 ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                No change
              </button>
              {TRIAL_PRESETS.map((d) => (
                <button
                  key={d}
                  onClick={() => { setTrialDays(d); setUseCustom(false); setCustomDays('') }}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    !useCustom && trialDays === d ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {d} days
                </button>
              ))}
              <input
                type="number"
                min="1"
                max="3650"
                placeholder="Custom"
                value={customDays}
                onChange={(e) => { setCustomDays(e.target.value); setUseCustom(true); setTrialDays(0) }}
                className={`w-24 px-3 py-1.5 rounded-full text-xs font-semibold border outline-none transition ${
                  useCustom && effectiveDays > 0 ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600'
                }`}
              />
            </div>
            {trialEnd && (
              <p className="text-xs text-gray-600">
                New trial end: <span className="font-semibold">{fmtDate(trialEnd)}</span>
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>
          )}

          {/* Confirm summary */}
          {confirming && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-gray-700 space-y-1">
              <p className="font-semibold text-gray-800">Apply these changes to “{org.name}”?</p>
              {planChanged && (
                <p>Plan: <span className="capitalize">{org.plan || 'starter'}</span> → <span className="font-semibold capitalize">{plan}</span></p>
              )}
              {effectiveDays > 0 && (
                <p>Trial: {effectiveDays} days from today, ending <span className="font-semibold">{fmtDate(trialEnd)}</span> (all members, org unlocked)</p>
              )}
              {compChanged && (
                <p>{comped
                  ? <>Access: <span className="font-semibold text-purple-700">comped — free forever, no billing</span></>
                  : <>Access: comp removed — back to <span className="font-semibold">trial rules</span></>}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex space-x-3 px-4 sm:px-6 py-4 border-t border-gray-200">
          <button
            onClick={confirming ? () => setConfirming(false) : onClose}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition"
          >
            {confirming ? 'Back' : 'Cancel'}
          </button>
          <button
            onClick={confirming ? handleConfirm : () => setConfirming(true)}
            disabled={!hasChanges || overLimit || saving}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
          >
            {saving ? 'Saving…' : confirming ? 'Yes, apply' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
